import React from 'react'
import { Query } from 'react-apollo'
import {
  CompliancePolicy,
  VulnerabilitySeverityBreakdown
} from '../applications-grid/shared'
import { AgentLanguage } from '../agent-language'
import { AttackStatus } from '../attack-status'
import { ServerEnvironment } from '../server-environment'
import { ApplicationLayerType } from './index'
import gql from 'graphql-tag'
import { vulnerabilitySeverityBreakdownAllFragment } from '../applications-grid/fragments'

const technologyBreakdownFragment = gql`
  fragment TechnologyBreakdown on ApplicationTechnology {
    name
    libraries {
      name
      hash
      vulnerable
    }
  }
`

const technologiesBreakdownFragment = gql`
  fragment TechnologiesBreakdown on TechnologiesBreakdown {
    view {
      ...TechnologyBreakdown
    }
    presentation {
      ...TechnologyBreakdown
    }
    service {
      ...TechnologyBreakdown
    }
  }
  ${technologyBreakdownFragment}
`

const applicationArchitectureQuery = gql`
  query ApplicationArchitecture($organizationID: ID!, $applicationID: ID!) {
    applicationArchitecture(
      organizationID: $organizationID
      applicationID: $applicationID
    ) {
      id
      language
      vulnerabilityBreakdown {
        ...VulnerabilitySeverityBreakdownAll
      }
      attackStatus
      hasAssess
      hasAProtectedServer
      online
      violatedCompliancePolicies {
        id
        name
      }
      serverEnvironments
      technologiesBreakdown {
        ...TechnologiesBreakdown
      }
    }
  }

  ${technologiesBreakdownFragment}
  ${vulnerabilitySeverityBreakdownAllFragment}
`

interface Library {
  readonly name: string
  readonly hash: string
  readonly vulnerable?: boolean
}

export interface ApplicationTechnology {
  readonly name: string
  readonly libraries: Library[]
}

export type ApplicationTechnologiesBreakdown = {
  readonly [key in ApplicationLayerType]: ApplicationTechnology[]
}

export interface ApplicationArchitecture {
  readonly id: string
  readonly language: AgentLanguage
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly attackStatus: AttackStatus
  readonly hasAssess: boolean
  readonly hasAProtectedServer: boolean
  readonly online: boolean
  readonly violatedCompliancePolicies: CompliancePolicy[]

  readonly serverEnvironments: ServerEnvironment[]
  readonly technologiesBreakdown: ApplicationTechnologiesBreakdown
}

interface ChildProps {
  readonly applicationArchitecture?: ApplicationArchitecture
  readonly loading: boolean
}

interface Props {
  readonly applicationID: string
  readonly organizationID: string
  readonly children: (props: ChildProps) => React.ReactNode
}

interface VarsWithoutAgg {
  readonly organizationID: string
  readonly applicationID: string
}

type Vars = VarsWithoutAgg

// TODO flowmap - add tests CONTRAST-37856
export function ApplicationArchitectureLoader({
  applicationID,
  organizationID,
  children
}: Props) {
  const variables: Vars = Object.assign(
    {},
    {
      organizationID,
      applicationID
    }
  )

  return (
    <Query
      fetchPolicy="network-only"
      query={applicationArchitectureQuery}
      variables={variables}
      errorPolicy="all"
    >
      {(result) => {
        const { data, error, loading } = result
        if (error) {
          throw new Error(error.message)
        }

        const appArchitecture = data && data.applicationArchitecture

        return children({
          applicationArchitecture: !!appArchitecture
            ? normalizeAppArchitecture(appArchitecture)
            : appArchitecture,
          loading
        })
      }}
    </Query>
  )
}

const normalizeAppArchitecture = (appArchitecture): ApplicationArchitecture => {
  return {
    ...appArchitecture,
    language: AgentLanguage.fromKey(appArchitecture.language),
    attackStatus: appArchitecture.attackStatus as AttackStatus
  }
}
